import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  Label,
  Modal,
  NexSays,
  Select,
  Textarea,
  cn,
  PageHeader,
} from '@nexply/ui';
import type {
  MatchDTO,
  MatchFactorDTO,
  MatchStatus,
  MatchSuggestionDTO,
  MemberDTO,
} from '@nexply/api-client';
import { api } from '../../lib/api.js';

type Tab = 'suggest' | 'saved' | 'history';

const STATUS_LABEL: Record<MatchStatus, string> = {
  SUGGESTED: 'Vorgeschlagen',
  CONNECTED: 'Verbunden',
  REJECTED: 'Verworfen',
  SAVED: 'Gemerkt',
};

const STATUS_TONE: Record<MatchStatus, 'gold' | 'green' | 'neutral'> = {
  SUGGESTED: 'gold',
  CONNECTED: 'green',
  REJECTED: 'neutral',
  SAVED: 'gold',
};

const FACTOR_TONE: Record<MatchFactorDTO['weight'], 'green' | 'gold' | 'neutral'> = {
  strong: 'green',
  medium: 'gold',
  light: 'neutral',
};

const FACTOR_HINT: Record<MatchFactorDTO['weight'], string> = {
  strong: 'starkes Signal',
  medium: 'mittleres Signal',
  light: 'schwaches Signal',
};

export function MatchesPage() {
  const [tab, setTab] = useState<Tab>('suggest');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vernetzer"
        subtitle="Wer in deinem Netzwerk passt zu dir — und was sagst du im ersten Gespräch?"
      />

      <div className="flex gap-1 border-b border-line">
        <TabBtn active={tab === 'suggest'} onClick={() => setTab('suggest')}>
          Offene Vorschläge
        </TabBtn>
        <TabBtn active={tab === 'saved'} onClick={() => setTab('saved')}>
          Gemerkt
        </TabBtn>
        <TabBtn active={tab === 'history'} onClick={() => setTab('history')}>
          Historie
        </TabBtn>
      </div>

      {tab === 'suggest' && <SuggestPanel />}
      {tab === 'saved' && <HistoryPanel fixedStatus="SAVED" />}
      {tab === 'history' && <HistoryPanel />}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition',
        active
          ? 'text-text border-gold'
          : 'text-text-muted border-transparent hover:text-text',
      )}
    >
      {children}
    </button>
  );
}

function memberLine(m: MemberDTO): string {
  return [m.role, m.company].filter(Boolean).join(' · ');
}

function SuggestPanel() {
  const qc = useQueryClient();
  const [memberId, setMemberId] = useState<string>('');
  const [useAi, setUseAi] = useState<boolean>(false);
  // Bearbeitete Vorschläge sofort ausblenden — der Server liefert sie erst nach Refetch nicht mehr.
  const [acted, setActed] = useState<Record<string, MatchStatus>>({});
  const [profileOf, setProfileOf] = useState<MemberDTO | null>(null);
  const [explainWith, setExplainWith] = useState<MemberDTO | null>(null);
  // Zuletzt angenommenes Match — kurze Bestätigung im Kopf der Seite.
  const [connected, setConnected] = useState<MatchDTO | null>(null);

  const membersQ = useQuery({
    queryKey: ['members', { all: true, includeSelf: true }],
    queryFn: () => api.members.list({ limit: 200, includeSelf: true }),
  });

  const options = useMemo(() => {
    const all = (membersQ.data?.members ?? []).filter((m) => m.status !== 'ARCHIVED');
    const self = all.filter((m) => m.isSelf);
    const rest = all.filter((m) => !m.isSelf);
    return { self, rest };
  }, [membersQ.data]);

  // Das wichtigste Match ist "wer passt zu MIR?" — deshalb ist das eigene Profil die Vorauswahl.
  useEffect(() => {
    if (!memberId && options.self[0]) setMemberId(options.self[0].id);
  }, [memberId, options.self]);

  const suggestQ = useQuery({
    // Eigener Namespace: ein Invalidieren von ['matches'] (nach jeder Aktion)
    // würde sonst den KI-Rerank erneut abrechnen. Neu geladen wird auf Knopfdruck.
    queryKey: ['match-suggest', memberId, useAi],
    queryFn: () => api.matches.suggest(memberId, { ai: useAi }),
    enabled: !!memberId,
    staleTime: 60_000,
  });

  const act = useMutation({
    mutationFn: (input: { suggestion: MatchSuggestionDTO; status: MatchStatus }) =>
      api.matches.create({
        memberAId: memberId,
        memberBId: input.suggestion.candidate.id,
        score: input.suggestion.score,
        reasons: input.suggestion.reasons,
        status: input.status,
      }),
    onSuccess: (res, vars) => {
      setActed((prev) => ({ ...prev, [vars.suggestion.candidate.id]: vars.status }));
      if (vars.status === 'CONNECTED') setConnected(res.match);
      qc.invalidateQueries({ queryKey: ['matches'] });
    },
  });

  const target = suggestQ.data?.target ?? null;
  const suggestions = (suggestQ.data?.suggestions ?? []).filter((s) => !acted[s.candidate.id]);
  const actedCount = Object.keys(acted).length;

  return (
    <div className="space-y-4">
      <Card>
        <CardBody className="space-y-3">
          <Label>Match-Basis</Label>
          <div className="flex gap-2">
            <Select
              value={memberId}
              onChange={(e) => {
                setMemberId(e.currentTarget.value);
                setActed({});
              }}
              className="flex-1"
            >
              <option value="">— wählen —</option>
              {options.self.length > 0 && (
                <optgroup label="Ich">
                  {options.self.map((m) => (
                    <option key={m.id} value={m.id}>
                      ★ Mein Profil — {m.name}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Mitglieder">
                {options.rest.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                    {m.company ? ` · ${m.company}` : ''}
                  </option>
                ))}
              </optgroup>
            </Select>
            <Button
              onClick={() => {
                setActed({});
                qc.invalidateQueries({ queryKey: ['match-suggest', memberId, useAi] });
              }}
              disabled={!memberId || suggestQ.isFetching}
            >
              {suggestQ.isFetching ? 'Lädt …' : 'Vorschläge laden'}
            </Button>
          </div>
          {target?.isSelf && (
            <p className="text-[11px] text-text-muted">
              Basis ist dein eigenes Profil: Wir suchen die Kontakte in deinem Netzwerk, die zu dir passen.
            </p>
          )}
          <div className="flex items-center justify-between rounded-xl border border-line bg-raise px-3 py-2.5">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="text-lg">✨</span>
              <div className="min-w-0">
                <div className="text-sm font-medium text-text">Nexply-AI nutzen</div>
                <div className="text-[11px] text-text-muted">
                  Bewertet Angebot &amp; Suche deiner Kontakte und schlägt die stärksten Matches vor.
                </div>
              </div>
            </div>
            <label
              className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition"
              style={{ background: useAi ? 'linear-gradient(135deg,var(--gold-light),var(--gold))' : 'var(--line-strong)' }}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={useAi}
                onChange={(e) => setUseAi(e.currentTarget.checked)}
              />
              <span className={`inline-block h-4 w-4 transform rounded-full bg-text transition ${useAi ? 'translate-x-6' : 'translate-x-1'}`} />
            </label>
          </div>
        </CardBody>
      </Card>

      {connected && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-success/25 bg-success/10 px-3 py-2.5">
          <div className="text-sm text-text">
            Match mit <span className="font-semibold">{connected.memberB.name}</span> angenommen.
          </div>
          <Button variant="ghost" onClick={() => setConnected(null)}>Schließen</Button>
        </div>
      )}

      {actedCount > 0 && (
        <div className="text-xs text-text-muted">
          {actedCount} Vorschlag{actedCount === 1 ? '' : 'e'} bearbeitet — zu finden unter „Gemerkt“ bzw. „Historie“.
        </div>
      )}

      {!memberId && (
        <EmptyState
          mascot="search"
          title="Match-Vorschläge"
          description="Wähle dein Profil oder ein Mitglied, um passende Verbindungen zu finden."
        />
      )}

      {memberId && suggestQ.isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-32 rounded-xl border border-line bg-raise animate-pulse"
            />
          ))}
        </div>
      )}

      {memberId && suggestQ.data && suggestions.length === 0 && (
        <EmptyState
          icon="∅"
          title={actedCount > 0 ? 'Alles bearbeitet' : 'Keine offenen Vorschläge'}
          description={
            actedCount > 0
              ? 'Du hast alle Vorschläge zu dieser Person bearbeitet.'
              : 'Aktuell keine passenden Verbindungen gefunden. Ergänze Tags, „Bietet“ oder „Sucht“ bei deinen Mitgliedern.'
          }
        />
      )}

      {suggestions.length > 0 && target && (
        <div className="space-y-3">
          {suggestions.map((s) => (
            <SuggestionCard
              key={s.candidate.id}
              target={target}
              suggestion={s}
              onAct={(status) => act.mutate({ suggestion: s, status })}
              onOpenProfile={() => setProfileOf(s.candidate)}
              onExplain={() => setExplainWith(s.candidate)}
              pending={act.isPending}
            />
          ))}
        </div>
      )}

      <MemberProfileModal member={profileOf} onClose={() => setProfileOf(null)} />
      {target && (
        <ExplainModal
          a={target}
          b={explainWith}
          onClose={() => setExplainWith(null)}
        />
      )}
    </div>
  );
}

function ScoreDial({ score, aiPowered }: { score: number; aiPowered?: boolean }) {
  return (
    <div className="flex flex-col items-end gap-1 shrink-0">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">Score</div>
      <div className="text-2xl font-bold text-gold leading-none">{score}</div>
      <div className="text-[10px] text-text-muted">
        {aiPowered ? 'von 100' : 'Heuristik-Punkte'}
      </div>
      {aiPowered && (
        <div className="text-[10px] uppercase tracking-wider text-gold-light/80 flex items-center gap-1">
          <span>✨</span> Nexply-AI
        </div>
      )}
    </div>
  );
}

function FactorList({ factors }: { factors: MatchFactorDTO[] }) {
  if (factors.length === 0) {
    return (
      <p className="text-xs text-text-muted">
        Keine harten Gemeinsamkeiten in den Profildaten — ergänze Tags, „Bietet“ und „Sucht“.
      </p>
    );
  }
  return (
    <ul className="space-y-1.5">
      {factors.map((f) => (
        <li key={f.key} className="flex items-start gap-2 text-xs">
          <Badge tone={FACTOR_TONE[f.weight]}>{FACTOR_HINT[f.weight]}</Badge>
          <span className="text-text-dim">
            <span className="text-text font-medium">{f.label}</span>
            {f.detail && <span className="text-text-muted"> — {f.detail}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

function SuggestionCard({
  target,
  suggestion,
  onAct,
  onOpenProfile,
  onExplain,
  pending,
}: {
  target: MemberDTO;
  suggestion: MatchSuggestionDTO;
  onAct: (status: MatchStatus) => void;
  onOpenProfile: () => void;
  onExplain: () => void;
  pending: boolean;
}) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const c = suggestion.candidate;

  return (
    <div className="rounded-xl border border-line bg-raise p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 gap-3">
          <Avatar name={c.name} src={c.photoUrl ?? undefined} size="md" />
          <div className="min-w-0">
            <button
              onClick={onOpenProfile}
              className="text-left text-base font-semibold text-text hover:text-gold transition"
            >
              {c.name}
            </button>
            <div className="text-xs text-text-dim">{memberLine(c) || '—'}</div>
            <div className="mt-0.5 text-[11px] text-text-muted">
              passt zu {target.isSelf ? 'dir' : target.name}
            </div>
            {suggestion.reasons.length > 0 && (
              <ul className="mt-2 space-y-1">
                {suggestion.reasons.map((r, i) => (
                  <li key={i} className="text-xs text-text-dim flex gap-1.5">
                    <span className="text-gold">›</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <ScoreDial score={suggestion.score} aiPowered={suggestion.aiPowered} />
      </div>

      <div>
        <button
          onClick={() => setShowBreakdown((v) => !v)}
          className="text-[11px] text-text-muted hover:text-text transition"
        >
          {showBreakdown ? '▾' : '▸'} Wie kommt der Score zustande? ({suggestion.factors.length} Signal
          {suggestion.factors.length === 1 ? '' : 'e'})
        </button>
        {showBreakdown && (
          <div className="mt-2 rounded-lg border border-line bg-raise p-3">
            <FactorList factors={suggestion.factors} />
            {suggestion.aiPowered && (
              <p className="mt-2 text-[11px] text-text-muted">
                Der Score selbst kommt von der KI (0–100) und gewichtet diese Signale inhaltlich.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onOpenProfile}>
            Profil ansehen
          </Button>
          <Button variant="ghost" onClick={onExplain}>
            ✨ Warum passt das?
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" disabled={pending} onClick={() => onAct('REJECTED')}>
            Ablehnen
          </Button>
          <Button variant="ghost" disabled={pending} onClick={() => onAct('SAVED')}>
            Für später merken
          </Button>
          <Button disabled={pending} onClick={() => onAct('CONNECTED')}>
            Annehmen
          </Button>
        </div>
      </div>
    </div>
  );
}

function MemberProfileModal({
  member,
  onClose,
}: {
  member: MemberDTO | null;
  onClose: () => void;
}) {
  const m = member;
  return (
    <Modal open={!!m} onClose={onClose} title={m?.name ?? ''} description={m ? memberLine(m) : undefined}>
      {m && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Avatar name={m.name} src={m.photoUrl ?? undefined} size="lg" />
            <div className="text-sm text-text-dim">
              {[m.industry, [m.city, m.country].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || '—'}
            </div>
          </div>

          {m.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {m.tags.map((t) => (
                <Badge key={t} tone="neutral">{t}</Badge>
              ))}
            </div>
          )}

          <ProfileField label="Pitch" value={m.pitch} />
          <ProfileField label="Bietet" value={m.offering} />
          <ProfileField label="Sucht" value={m.seeking} />
          <ProfileField label="Notizen" value={m.notes} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {m.email && <ContactLink label="E-Mail" href={`mailto:${m.email}`} value={m.email} />}
            {m.phone && <ContactLink label="Telefon" href={`tel:${m.phone}`} value={m.phone} />}
            {m.website && <ContactLink label="Website" href={m.website} value={m.website} />}
            {m.linkedin && <ContactLink label="LinkedIn" href={m.linkedin} value={m.linkedin} />}
          </div>
        </div>
      )}
    </Modal>
  );
}

function ProfileField({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-text-muted">{label}</div>
      <p className="mt-0.5 text-sm text-text-dim whitespace-pre-wrap">{value}</p>
    </div>
  );
}

function ContactLink({ label, href, value }: { label: string; href: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wider text-text-muted">{label}</div>
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="block truncate text-sm text-gold hover:underline"
      >
        {value}
      </a>
    </div>
  );
}

function ExplainModal({
  a,
  b,
  onClose,
}: {
  a: MemberDTO;
  b: MemberDTO | null;
  onClose: () => void;
}) {
  const q = useQuery({
    // Bewusst NICHT unter ['matches'] — sonst würde jede Match-Aktion die
    // Invalidierung mitziehen und einen neuen (teuren) Sonnet-Call auslösen.
    queryKey: ['match-explain', a.id, b?.id],
    queryFn: () => api.matches.explain({ memberAId: a.id, memberBId: b!.id }),
    enabled: !!b,
    // Eine Erklärung kostet einen Sonnet-Call: einmal geholt, bleibt sie in der Session gecached.
    staleTime: Infinity,
    gcTime: 60 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const ex = q.data?.explanation;

  return (
    <Modal
      open={!!b}
      onClose={onClose}
      title={b ? `${a.isSelf ? 'Du' : a.name} ↔ ${b.name}` : ''}
      description="Warum sich diese Verbindung lohnt"
    >
      {q.isLoading && (
        <div className="space-y-3">
          <NexSays thinking>Ich schaue mir beide Profile an …</NexSays>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-16 rounded-lg border border-line bg-raise animate-pulse" />
          ))}
        </div>
      )}

      {q.isError && (
        <div className="space-y-3">
          <NexSays degraded title="Das hat nicht geklappt">
            Die Erklärung konnte nicht geladen werden.
          </NexSays>
          <Button variant="ghost" onClick={() => q.refetch()}>Erneut versuchen</Button>
        </div>
      )}

      {ex && (
        <div className="space-y-4">
          {!ex.aiPowered && (
            <NexSays degraded title="Ohne KI">
              {ex.degradedReason ?? 'KI-Erklärung nicht verfügbar.'} Du siehst die erkannten
              Gemeinsamkeiten aus den Profildaten.
            </NexSays>
          )}

          <ExplainSection title="Warum das passt" text={ex.fit} />
          <ExplainSection title="Konkreter Mehrwert" text={ex.value} />

          {ex.projects.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-text-muted">
                Mögliche gemeinsame Projekte
              </div>
              <ul className="mt-1 space-y-1">
                {ex.projects.map((p, i) => (
                  <li key={i} className="flex gap-2 text-sm text-text-dim">
                    <span className="text-gold">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ExplainSection title="Business-Potenzial" text={ex.potential} />

          {ex.opener && (
            <div className="rounded-lg border border-gold/25 bg-gold/[0.06] p-3">
              <div className="text-[11px] uppercase tracking-wider text-gold-light/80">
                Gesprächsaufhänger
              </div>
              <p className="mt-1 text-sm text-text italic">„{ex.opener}“</p>
            </div>
          )}

          {ex.signals.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-text-muted">
                Erkannte Gemeinsamkeiten
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {ex.signals.map((s, i) => (
                  <Badge key={i} tone="neutral">{s}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function ExplainSection({ title, text }: { title: string; text: string }) {
  if (!text) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-text-muted">{title}</div>
      <p className="mt-0.5 text-sm text-text-dim whitespace-pre-wrap">{text}</p>
    </div>
  );
}

function HistoryPanel({ fixedStatus }: { fixedStatus?: MatchStatus }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<MatchStatus | ''>('');
  const effective = fixedStatus ?? (status || undefined);
  const [profileOf, setProfileOf] = useState<MemberDTO | null>(null);
  const [explainPair, setExplainPair] = useState<{ a: MemberDTO; b: MemberDTO } | null>(null);

  const list = useQuery({
    queryKey: ['matches', { status: effective ?? null }],
    queryFn: () => api.matches.list({ status: effective }),
  });

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: { status?: MatchStatus; notes?: string | null } }) =>
      api.matches.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['matches'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.matches.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['matches'] }),
  });

  const matches = list.data?.matches ?? [];

  return (
    <div className="space-y-4">
      {!fixedStatus && (
        <Card>
          <CardBody>
            <div className="flex items-center gap-3">
              <Label className="mb-0">Status</Label>
              <Select
                value={status}
                onChange={(e) => setStatus(e.currentTarget.value as MatchStatus | '')}
                className="w-48"
              >
                <option value="">Alle</option>
                <option value="SUGGESTED">Vorgeschlagen</option>
                <option value="CONNECTED">Verbunden</option>
                <option value="SAVED">Gemerkt</option>
                <option value="REJECTED">Verworfen</option>
              </Select>
            </div>
          </CardBody>
        </Card>
      )}

      {list.isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-28 rounded-xl border border-line bg-raise animate-pulse"
            />
          ))}
        </div>
      )}

      {!list.isLoading && matches.length === 0 && (
        <EmptyState
          icon={fixedStatus === 'SAVED' ? '🔖' : '📋'}
          title={fixedStatus === 'SAVED' ? 'Nichts gemerkt' : 'Noch keine Matches gespeichert'}
          description={
            fixedStatus === 'SAVED'
              ? 'Merke dir Vorschläge mit „Für später merken“ — sie landen hier.'
              : 'Bearbeite Match-Vorschläge im Tab „Offene Vorschläge“.'
          }
        />
      )}

      {matches.length > 0 && (
        <div className="space-y-3">
          {matches.map((m) => (
            <HistoryCard
              key={m.id}
              match={m}
              onUpdate={(input) => update.mutate({ id: m.id, input })}
              onDelete={() => {
                if (confirm('Match wirklich löschen?')) remove.mutate(m.id);
              }}
              onOpenProfile={setProfileOf}
              onExplain={() => setExplainPair({ a: m.memberA, b: m.memberB })}
              pending={update.isPending || remove.isPending}
            />
          ))}
        </div>
      )}

      <MemberProfileModal member={profileOf} onClose={() => setProfileOf(null)} />
      {explainPair && (
        <ExplainModal a={explainPair.a} b={explainPair.b} onClose={() => setExplainPair(null)} />
      )}
    </div>
  );
}

function HistoryCard({
  match,
  onUpdate,
  onDelete,
  onOpenProfile,
  onExplain,
  pending,
}: {
  match: MatchDTO;
  onUpdate: (input: { status?: MatchStatus; notes?: string | null }) => void;
  onDelete: () => void;
  onOpenProfile: (m: MemberDTO) => void;
  onExplain: () => void;
  pending: boolean;
}) {
  const [notes, setNotes] = useState(match.notes ?? '');
  const dirty = useMemo(() => (notes || '') !== (match.notes ?? ''), [notes, match.notes]);

  return (
    <div className="rounded-xl border border-line bg-raise p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm">
            <button
              onClick={() => onOpenProfile(match.memberA)}
              className="text-text font-semibold hover:text-gold transition"
            >
              {match.memberA.isSelf ? `Mein Profil (${match.memberA.name})` : match.memberA.name}
            </button>
            <span className="text-gold mx-2">↔</span>
            <button
              onClick={() => onOpenProfile(match.memberB)}
              className="text-text font-semibold hover:text-gold transition"
            >
              {match.memberB.name}
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Badge tone={STATUS_TONE[match.status]}>{STATUS_LABEL[match.status]}</Badge>
            {match.reasons.map((r, i) => (
              <Badge key={i} tone="neutral">{r}</Badge>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <div className="text-xs text-text-muted">Score</div>
          <div className="text-xl font-bold text-gold">{match.score}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-2 items-start">
        <Select
          value={match.status}
          onChange={(e) => onUpdate({ status: e.currentTarget.value as MatchStatus })}
          disabled={pending}
        >
          <option value="SUGGESTED">Vorgeschlagen</option>
          <option value="CONNECTED">Verbunden</option>
          <option value="SAVED">Gemerkt</option>
          <option value="REJECTED">Verworfen</option>
        </Select>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          placeholder="Eigene Notiz zu diesem Match …"
          className="min-h-[40px]"
        />
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" onClick={onExplain}>
            ✨ Warum passt das?
          </Button>
          <Button
            variant="ghost"
            disabled={!dirty || pending}
            onClick={() => onUpdate({ notes: notes || null })}
          >
            Notiz speichern
          </Button>
          <Button variant="danger" disabled={pending} onClick={onDelete}>
            Löschen
          </Button>
        </div>
      </div>
    </div>
  );
}
